import type { NFTEscrowAccount, NFTPoolOrder, NFTSingleOrder } from "@stepfinance/nft";
import React, { useContext, useEffect, useMemo, useState } from "react";

import { useTensorOrders } from "../hooks/nftMarkets/useTensorOrders";
import { useHadeswapOrdersQuery } from "../hooks/queries/nftMarkets/useHadeswapOrdersQuery";

export interface NFTMarketsContextState {
  singleOrders: { totalValue: number; positions: NFTSingleOrder[] };
  poolOrders: { totalValue: number; positions: NFTPoolOrder[] };
  escrowAccounts: { totalValue: number; positions: NFTEscrowAccount[] };
  nftMarketsBalance: number;
  setNFTMarketsBalance: React.Dispatch<React.SetStateAction<number>>;
}

export type RefreshContextUpdaterProps = () => Promise<void>;

const NFTMarketsContext = React.createContext<null | NFTMarketsContextState>(null);

const createTotalValueObj = (positions) => {
  let totalValue = 0;
  for (let i = 0; i < positions.length; i++) {
    const position = positions[i];
    totalValue += position.value;
  }
  return {
    positions,
    totalValue,
  };
};

export function NFTMarketsContextProvider(props: any) {
  const [nftMarketsBalance, setNFTMarketsBalance] = useState(0);

  const {
    singleOrders: tensorSingleOrders,
    poolOrders: tensorPoolOrders,
    escrowAccounts: tensorEscrowAccounts,
  } = useTensorOrders();

  const { data: hadeswapPoolOrders } = useHadeswapOrdersQuery();

  const singleOrders = useMemo(() => createTotalValueObj([...tensorSingleOrders]), [tensorSingleOrders]);

  const poolOrders = useMemo(
    () => createTotalValueObj([...tensorPoolOrders, ...(hadeswapPoolOrders ?? [])]),
    [hadeswapPoolOrders, tensorPoolOrders],
  );

  const escrowAccounts = useMemo(() => createTotalValueObj([...tensorEscrowAccounts]), [tensorEscrowAccounts]);

  useEffect(
    () => setNFTMarketsBalance(singleOrders.totalValue + poolOrders.totalValue + escrowAccounts.totalValue),
    [escrowAccounts.totalValue, poolOrders.totalValue, singleOrders.totalValue],
  );

  return (
    <NFTMarketsContext.Provider
      value={{
        singleOrders,
        poolOrders,
        escrowAccounts,
        nftMarketsBalance,
        setNFTMarketsBalance,
      }}
    >
      {props.children}
    </NFTMarketsContext.Provider>
  );
}

export function useNFTMarketsContext(): NFTMarketsContextState {
  const ctx = useContext(NFTMarketsContext);
  if (ctx === null) {
    throw new Error("Context not available");
  }
  return ctx;
}
